/**
 * A resilient fetch wrapper that automatically retries transient network errors.
 * Specifically useful for Chromium's 'net::ERR_NETWORK_CHANGED' error.
 * 
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @param {number} maxRetries - Maximum number of retries (default: 3)
 * @param {number} delay - Initial delay between retries in ms (default: 500)
 */
export const fetchWithRetry = async (url, options = {}, maxRetries = 5, delay = 500) => {
  let lastError;

  for (let i = 0; i <= maxRetries; i++) {
    try {
      if (i > 0) {
        console.warn(`⚠️ [Network] Retrying fetch (${i}/${maxRetries}) in ${delay * Math.pow(2, i - 1)}ms for: ${url}`);
        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i - 1)));
      }

      const response = await fetch(url, options);

      // If the server returns a transient error code (502 Bad Gateway, 503 Service Unavailable, 504 Gateway Timeout),
      // we should also retry because the backend might be reloading (e.g. uvicorn auto-reload)
      const isTransientServerError = response.status === 502 || response.status === 503 || response.status === 504;
      if (isTransientServerError && i < maxRetries) {
        console.warn(`⚠️ [Network] Server returned transient status ${response.status} for: ${url}. Retrying...`);
        continue;
      }

      return response;
    } catch (error) {
      lastError = error;
      
      // Standard fetch() only throws an exception on network failure (connection refused, DNS lookup fail, CORS error, aborted request).
      // If the user explicitly aborted the request, we should not retry.
      const isUserAbort = error.name === 'AbortError';
      
      if (isUserAbort || i === maxRetries) {
        throw error;
      }
      
      console.warn(`⚠️ [Network] Transient network error encountered (attempt ${i + 1}/${maxRetries + 1}):`, error.message || error);
      // Continue the loop to retry
    }
  }

  throw lastError;
};

export default fetchWithRetry;
